import { useMemo, useCallback, FC, useEffect } from 'react';
import { Alert, Group, Text, UnstyledButton } from '@mantine/core';

import {
    useBillingQuery,
    useProfileQuery
} from '@/services/customer/profile/profile.queries.ts';
import { StatusModeContent } from '@/components/BillingNotificationBanner/BillingNotificationBanner.utils.tsx';
import { cn } from '@/utils';

type BillingNotificationBannerProps = {
    className?: string;
};
const BillingNotificationBanner: FC<BillingNotificationBannerProps> = ({
    className
}) => {
    const { data: billing, isLoading } = useBillingQuery();
    const { data: profile } = useProfileQuery();

    const stage = billing?.subscription_info?.subscription_stage;

    const isTemporalProfile = profile?.status === 'temporal';
    const showBanner = Boolean(
        profile?.tenant === 'newo' &&
        (isTemporalProfile || (stage && stage !== 'paid'))
    );

    const content = useMemo(() => {
        if (stage === 'paid') {
            return;
        }

        if (isTemporalProfile) {
            return StatusModeContent.temporal;
        }

        if (!stage) {
            return;
        }
        return StatusModeContent[stage];
    }, [isTemporalProfile, stage]);

    useEffect(() => {
        const root = document.documentElement;
        if (showBanner && !isLoading) {
            return;
        }
        root.style.removeProperty('--billing-banner-height');
    }, [showBanner, isLoading]);

    const handleSetBillingBannerHeight = useCallback(
        (container: HTMLDivElement | null) => {
            if (container) {
                const { height } = container.getBoundingClientRect();
                document.documentElement.style.setProperty(
                    '--billing-banner-height',
                    `${height}px`
                );
            }
        },
        []
    );
    if (!content || isLoading || !showBanner) {
        return;
    }

    return (
        <Alert
            className={cn(className, {
                'bg-yellow-3': stage === 'development',
                'bg-red-3': isTemporalProfile,
                'bg-green-3': stage === 'trial'
            })}
            py={6}
            ref={handleSetBillingBannerHeight}
        >
            <Group align="flex-start" gap="xs" justify="center" wrap="nowrap">
                <Text className="!text-xs md:!text-base">
                    <strong>{content.title}</strong> {content.message}
                </Text>

                <UnstyledButton
                    component="a"
                    href={content.learnMoreButton.link}
                    key={content.learnMoreButton.link}
                    target="_blank"
                    w="fit-content"
                >
                    <Text
                        className="!text-xs md:!text-base border-black-7 border-b whitespace-nowrap"
                        fz={16}
                    >
                        {content.learnMoreButton.label}
                    </Text>
                </UnstyledButton>
            </Group>
        </Alert>
    );
};

export default BillingNotificationBanner;
